import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import style from "./SkillsDetails.module.css";
import { baseurl } from "../Url";
import {
  MdFlag,
  MdSchedule,
  MdCheckCircle,
  MdCalendarToday,
  MdPlayArrow,
  MdStar,
  MdPerson,
  MdBookmarkBorder,
  MdShare,
  MdCode,
  MdTimeline,
  MdSchool,
} from "react-icons/md";
import { PiListChecks } from "react-icons/pi";
import Loader from "../Loder/Loader";

const SkillsDetails = () => {
  const { learningId } = useParams();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem("USERID");
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});

  // Capture referral info (code + asset/skill) when user lands via shared link
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    // Support multiple possible param names for the code
    const rawCode = params.get("ref_code") || params.get("ref") || params.get("code");
    if (!rawCode) return;

    const fetchReferral = async () => {
      try {
        const res = await fetch(`${baseurl}/referral/link?code=${encodeURIComponent(rawCode)}`);
        const data = await res.json();

        if (data && data.status && data.data && data.data.referral_code) {
          // Core referral identifiers
          localStorage.setItem("duno_referral_code", data.data.referral_code);

          // Referrer user id (who shared the link)
          if (data.data.referrer_user_id) {
            localStorage.setItem("duno_referral_user_id", data.data.referrer_user_id);
          }

          // Asset / Skill metadata
          if (data.data.referral_asset_category) {
            localStorage.setItem("duno_referral_asset_category", data.data.referral_asset_category);
          }
          if (data.data.referral_asset_id) {
            // Treat this as the shared skill_id
            localStorage.setItem("duno_referral_asset_id", data.data.referral_asset_id);
          }
        } else {
          localStorage.removeItem("duno_referral_code");
          localStorage.removeItem("duno_referral_user_id");
          localStorage.removeItem("duno_referral_asset_category");
          localStorage.removeItem("duno_referral_asset_id");
        }
      } catch (e) {
        console.warn("Failed to capture referral from link:", e);
      }
    };

    fetchReferral();
  }, []);

  // Record referral visit once for logged-in visitors (non-referrer),
  // so referral list is populated even without signup flow.
  useEffect(() => {
    if (typeof window === "undefined" || !currentUserId) return;
    const referralCode = localStorage.getItem("duno_referral_code");
    const referrerUserId = localStorage.getItem("duno_referral_user_id");
    const assetCategory = localStorage.getItem("duno_referral_asset_category") || "SKILL";
    const assetId = localStorage.getItem("duno_referral_asset_id") || learningId;

    if (!referralCode || !referrerUserId || !assetId) return;
    if (String(referrerUserId) === String(currentUserId)) return;

    const dedupeKey = `duno_referral_visit_recorded_${referralCode}_${currentUserId}_${assetId}`;
    if (sessionStorage.getItem(dedupeKey) === "1") return;

    fetch(`${baseurl}/referral/record-action`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        referrer_user_id: referrerUserId,
        referee_id: currentUserId,
        referee_username: localStorage.getItem("username") || localStorage.getItem("email") || "",
        referral_asset_category: assetCategory,
        referral_asset_id: assetId,
        action: "LINK_VISIT",
      }),
    })
      .then(() => {
        sessionStorage.setItem(dedupeKey, "1");
      })
      .catch((err) => {
        console.warn("Failed to record referral visit:", err);
      });
  }, [currentUserId, learningId]);

  useEffect(() => {
    if (!learningId) {
      setError("Skill not found");
      setLoading(false);
      return;
    }

    const fetchSkill = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${baseurl}/skills/public/${encodeURIComponent(learningId)}`);
        const data = await res.json();

        if (data.status && data.data) {
          setSkill(data.data);
        } else {
          setError(data.description || "Skill not found");
        }
      } catch (err) {
        setError("Failed to load skill");
      } finally {
        setLoading(false);
      }
    };

    fetchSkill();
  }, [learningId]);

  const checkUserAndProceed = async (onSuccess) => {
    const userId = localStorage.getItem("USERID");
    if (!userId) {
      navigate("/login", { state: { from: `/skills/details/${learningId}` } });
      return;
    }
    try {
      const res = await fetch(`${baseurl}/check/user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      if (data.status) {
        onSuccess?.();
      } else {
        navigate("/login", { state: { from: `/skills/details/${learningId}` } });
      }
    } catch {
      navigate("/login", { state: { from: `/skills/details/${learningId}` } });
    }
  };

  const toggleModule = (index) => {
    setExpandedModules((prev) => ({ ...prev, [index]: prev[index] === false }));
  };

  const handleCourseClick = () => {
    checkUserAndProceed(() => {
      navigate("/");
    });
  };

  if (loading) {
    return (
      <div className={`${style.container} ${style.loadingState}`}>
        <Loader />
      </div>
    );
  }

  if (error || !skill) {
    return (
      <div className={`${style.container} ${style.errorState}`}>
        <div className={style.errorBox}>
          <p>{error || "Skill not found"}</p>
          <button onClick={() => navigate("/")}>Go to Home</button>
        </div>
      </div>
    );
  }

  const title = skill.CourseName || skill.category_details || "Skill";
  const categoryLabel = skill.category_details && skill.CourseName ? skill.category_details : null;
  const topics = skill.Topics || [];
  const moduleCount = topics.length;
  const totalTopicCount = topics.reduce((count, module) => count + (module.topics?.length || 0), 0);
  const overviewText =
    skill.description ||
    skill.summary ||
    skill.overview ||
    `Build practical skills through ${moduleCount} modules and ${totalTopicCount} guided topics.`;
  const updatedText =
    skill.updated_at || skill.updatedAt || skill.last_updated || skill.modified_at || null;

  const topicPreviewList = topics
    .flatMap((module) => (module.topics || []).map((topic) => topic.value || topic.title || topic.name))
    .filter(Boolean)
    .slice(0, 5);

  const pickList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string") {
      return value
        .split(/[,\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const techStackRaw = [
    ...pickList(skill.tech_stack),
    ...pickList(skill.techStack),
    ...pickList(skill.stack),
  ];
  const techStack = [...new Map(techStackRaw.map((t) => [String(t).toLowerCase(), t])).values()];

  const fallbackStack = ["React.js", "Node.js", "MongoDB", "REST APIs"];
  const techStackList = (techStack && techStack.length > 0 ? techStack : fallbackStack).slice(0, 7);

  const requirements = [
    ...pickList(skill.requirements),
    ...pickList(skill.prerequisites),
    ...pickList(skill.requirement),
  ];
  const requirementList =
    (requirements && requirements.length > 0 ? requirements : ["Basic knowledge of JavaScript", "Interest in building real-world apps"]).slice(0, 5);
  const progress = totalTopicCount > 0 ? Math.min(100, Math.round((Math.max(1, Math.floor(totalTopicCount * 0.3)) / totalTopicCount) * 100)) : 30;
  const relatedCourses = ["AI Foundations", "Full Stack Projects", "Backend APIs"];

  return (
    <div className={style.container}>
      <div className={style.content}>
        <div className={style.skillCard}>
          <header className={style.header}>
            <div className={style.headerShell}>
              <div className={style.headerTop}>
                <div className={style.headerInfo}>
                  <div className={style.titleWrap}>
                    {categoryLabel && (
                      <span className={style.categoryBadge}>{categoryLabel}</span>
                    )}
                    <h1>{title}</h1>
                    <div className={style.metaInline}>
                      <span className={style.metaInlineItem}>
                        <MdStar size={16} />
                        4.6
                      </span>
                      <span className={style.metaInlineItem}>
                        <MdPerson size={16} />
                        1.2K learners
                      </span>
                      {updatedText && (
                        <span className={style.metaInlineItem}>
                          <MdCalendarToday size={15} />
                          Updated {String(updatedText).slice(0, 10)}
                        </span>
                      )}
                      <span className={style.metaInlineItem}>
                        <MdSchool size={16} />
                        Dr. John
                      </span>
                    </div>
                    <div className={style.quickStats}>
                      <span className={style.quickStat}>
                        {moduleCount} Module{moduleCount !== 1 ? "s" : ""}
                      </span>
                      <span className={style.quickStat}>
                        {totalTopicCount} Topic{totalTopicCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={style.heroActions}>
                  <div className={style.actionTopRow}>
                    <button type="button" className={style.secondaryBtn}>
                      <MdFlag size={15} />
                      8 pSkill
                    </button>
                    <button type="button" className={style.iconBtn}>
                      <MdBookmarkBorder size={18} />
                    </button>
                  </div>
                  <button type="button" className={style.startBtn} onClick={handleCourseClick}>
                    Start Learning
                    <MdPlayArrow size={18} />
                  </button>
                </div>
              </div>
            </div>
          </header>

          <div className={style.profileBody}>
            <div className={style.pageGrid}>
              <div className={style.mainColumn}>
                <section className={style.infoPanel}>
                  <h3>Overview</h3>
                  <p>{overviewText}</p>
                </section>

                <section className={style.infoPanel}>
                  <h3>Tech Stack</h3>
                  <div className={style.stackWrap}>
                    {techStackList.map((tech, idx) => (
                      <span key={`${tech}-${idx}`} className={style.stackTag}>
                        <MdCode size={14} />
                        {tech}
                      </span>
                    ))}
                  </div>
                </section>

                {topics.length > 0 && (
                  <div className={style.curriculumSection}>
                    <div className={style.curriculumTop}>
                      <h2 className={style.curriculumHeading}>
                        <PiListChecks size={22} aria-hidden />
                        Curriculum
                      </h2>
                      <div className={style.progressWrap}>
                        <div className={style.progressTrack}>
                          <div className={style.progressBar} style={{ width: `${progress}%` }} />
                        </div>
                        <span>{progress}%</span>
                      </div>
                    </div>
                    <div className={style.modulesList}>
                      {topics.map((module, modIndex) => {
                        const moduleTopics = module.topics || [];
                        const moduleName = module.module_name || `Module ${modIndex + 1}`;
                        const isExpanded = expandedModules[modIndex] !== false;

                        return (
                          <div key={modIndex} className={style.moduleCard}>
                            <button
                              type="button"
                              className={style.moduleHeader}
                              onClick={() => toggleModule(modIndex)}
                              aria-expanded={isExpanded}
                            >
                              <span className={style.moduleNameWrap}>
                                <span className={style.moduleIndex}>{modIndex + 1}</span>
                                <span className={style.moduleName}>{moduleName}</span>
                              </span>
                              <span className={style.moduleCount}>
                                {moduleTopics.length} topic{moduleTopics.length !== 1 ? "s" : ""}
                              </span>
                              <span className={`${style.chevron} ${isExpanded ? style.chevronOpen : ""}`}>
                                ▼
                              </span>
                            </button>
                            {isExpanded && moduleTopics.length > 0 && (
                              <ul className={style.topicList}>
                                {moduleTopics.map((topic, topicIndex) => (
                                  <li
                                    key={topicIndex}
                                    className={style.topicItem}
                                    onClick={handleCourseClick}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === "Enter" && handleCourseClick()}
                                  >
                                    {topic.value || topic.title || topic.name || `Topic ${topicIndex + 1}`}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <aside className={style.sideColumn}>
                <section className={style.sideCard}>
                  <h3>Project Output</h3>
                  <ul className={style.sideList}>
                    {(topicPreviewList.length > 0 ? topicPreviewList : ["Structured curriculum", "Hands-on modules", "Real project practice"]).map((item, idx) => (
                      <li key={`${item}-${idx}`}>
                        <MdCheckCircle size={16} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </section>

                <section className={style.sideCard}>
                  <h3>Requirements</h3>
                  <ul className={style.sideList}>
                    {requirementList.map((item, idx) => (
                      <li key={`${item}-${idx}`}>
                        <MdCheckCircle size={16} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </section>

                <section className={style.sideCard}>
                  <h3>Related Courses</h3>
                  <ul className={style.sideList}>
                    {relatedCourses.map((item) => (
                      <li key={item}>
                        <MdTimeline size={16} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </section>

                <section className={style.sideCard}>
                  <h3>Reviews & Feedback</h3>
                  <div className={style.feedbackGrid}>
                    <div className={style.feedbackItem}>
                      <div className={style.feedbackHead}>
                        <span>Priya S.</span>
                        <span className={style.feedbackRating}>
                          <MdStar size={14} />
                          4.8
                        </span>
                      </div>
                      <p>Very clear structure and practical module flow.</p>
                    </div>
                    <div className={style.feedbackItem}>
                      <div className={style.feedbackHead}>
                        <span>Arjun K.</span>
                        <span className={style.feedbackRating}>
                          <MdStar size={14} />
                          4.7
                        </span>
                      </div>
                      <p>Great for understanding real-world consultation workflows.</p>
                    </div>
                    <div className={style.feedbackItem}>
                      <div className={style.feedbackHead}>
                        <span>Neha R.</span>
                        <span className={style.feedbackRating}>
                          <MdStar size={14} />
                          4.9
                        </span>
                      </div>
                      <p>Loved the curriculum quality and clean progression.</p>
                    </div>
                  </div>
                </section>

                <section className={style.sideCard}>
                  <h3>Learning Outcomes</h3>
                  <ul className={style.sideList}>
                    <li>
                      <MdCheckCircle size={16} />
                      Build complete consultation app workflow knowledge
                    </li>
                    <li>
                      <MdCheckCircle size={16} />
                      Understand frontend + backend integration patterns
                    </li>
                    <li>
                      <MdCheckCircle size={16} />
                      Apply auth, scheduling, and API best practices
                    </li>
                    <li>
                      <MdCheckCircle size={16} />
                      Ship a real project-ready portfolio simulation
                    </li>
                    <li>
                      <MdCheckCircle size={16} />
                      Design consultation flows with reusable UI components
                    </li>
                    <li>
                      <MdCheckCircle size={16} />
                      Structure backend endpoints for scalable feature growth
                    </li>
                    <li>
                      <MdCheckCircle size={16} />
                      Validate inputs and handle edge cases in core workflows
                    </li>
                    <li>
                      <MdCheckCircle size={16} />
                      Improve deployment readiness with clear module-based architecture
                    </li>
                  </ul>
                </section>
              </aside>
            </div>

            {(!topics || topics.length === 0) && (
              <div className={style.curriculumSection}>
                <p className={style.emptyMsg}>No curriculum available.</p>
                </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsDetails;
